import './Analytics.scss';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SideBar from '../../components/SideBar/SideBar';
import TopNav from '../../components/TopNav/TopNav';
import api from '../../services/api';
import { colors } from '../../styles/tokens';
import { coordsToCity } from '../../utils/geocode';

const EVENT_TYPE_LABELS = {
  venue_view: 'Users Who Viewed Venues',
  event_view: 'Users Who Viewed Events',
  search: 'Search Log',
  venue_tap: 'Users Who Tapped a Venue',
  event_tap: 'Users Who Tapped an Event',
  bottle_service_view: 'Users Who Viewed Bottle Service',
  bottle_purchase_started: 'Users Who Started a Purchase',
  ai_query: 'Users Who Used AI',
  map_opened: 'Users Who Opened the Map',
  venue_favorited: 'Users Who Favorited a Venue',
  view_tables_clicked: 'Users Who Viewed Tables',
  join_guestlist_clicked: 'Users Who Joined a Guest List',
  ad_impression: 'Users Who Saw an Ad',
  ad_click: 'Users Who Tapped an Ad',
  ad_dismiss: 'Users Who Dismissed an Ad',
  app_update_popup_shown: 'Users Who Saw the App Popup',
  app_update_popup_tapped: 'Users Who Tapped the App Popup',
  force_update_shown: 'Users Who Hit the Update Screen',
  force_update_tapped: 'Users Who Tapped Update',
};

const AnalyticsDetail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [logRows, setLogRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const eventType = searchParams.get('event_type');
  const venueId = searchParams.get('venue_id');
  const venueName = searchParams.get('venue_name');
  const searchQuery = searchParams.get('query');
  const days = searchParams.get('days') || 30;

  // Search doesn't fit the "who did this and how many times" shape the
  // rest of this page uses -- the query text itself is the point, and it
  // needs to read as a chronological log, not folded into a per-user count
  // alongside email/location, none of which matter here.
  const isSearchLog = eventType === 'search';

  const title = searchQuery
    ? `Users who searched "${searchQuery}"`
    : venueName
      ? `Users who viewed ${venueName}`
      : EVENT_TYPE_LABELS[eventType] || eventType || 'User Activity';

  const fetchSearchLog = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/analytics/event-log?days=${days}&event_type=search&limit=100`);
      // The mobile SearchBar can fire performSearch() twice for one tap
      // (onSubmitEditing + button onPress both firing) -- collapse an
      // exact {user_id, query, created_at} repeat into one row rather than
      // showing the same real search twice. Real repeats at a different
      // timestamp stay as separate rows.
      const seen = new Set();
      const deduped = (data.data || []).filter((row) => {
        const key = `${row.user_id}|${row.query}|${row.created_at}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setLogRows(deduped);
    } catch (error) {
      console.error('Error fetching search log:', error);
    } finally {
      setLoading(false);
    }
  }, [days]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days, limit: 50 });
      if (eventType) params.set('event_type', eventType);
      if (venueId) params.set('venue_id', venueId);
      if (searchQuery) params.set('query', searchQuery);

      const { data } = await api.get(`/analytics/users-by-action?${params}`);
      // Fetch location from /users endpoint (same as profile page), then
      // resolve raw "lat, lng" coordinates to a readable city.
      const enriched = await Promise.all(data.data.map(async (u) => {
        try {
          const { data: userData } = await api.get(`/users/${u.user_id}`);
          const location = await coordsToCity(userData.user?.location || '');
          return { ...u, location };
        } catch { return u; }
      }));
      setUsers(enriched);
    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setLoading(false);
    }
  }, [eventType, venueId, searchQuery, days]);

  useEffect(() => {
    if (isSearchLog) {
      fetchSearchLog();
    } else {
      fetchUsers();
    }
  }, [isSearchLog, fetchSearchLog, fetchUsers]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="analytics">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
              <button
                onClick={() => navigate('/analytics')}
                style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, padding: '8px 16px', color: '#fff', cursor: 'pointer', fontSize: 14 }}>
                Back
              </button>
              <h1 className="analytics__title" style={{ margin: 0 }}>{title}</h1>
            </div>

            <div className="analytics__table" style={{ maxWidth: '100%' }}>
              {loading ? (
                <div className="analytics__loading">Loading...</div>
              ) : isSearchLog ? (
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 160 }}>Date</th>
                      <th>Searched For</th>
                      <th>User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logRows.map((r, i) => (
                      <tr
                        key={i}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/users/${r.user_id}?tab=activity`)}
                      >
                        <td style={{ color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap' }}>{formatDate(r.created_at)}</td>
                        <td style={{ color: '#fff' }}>"{r.query}"</td>
                        <td style={{ color: colors.brand, fontWeight: 600 }}>
                          {r.firstName || r.lastName ? `${r.firstName} ${r.lastName}`.trim() : '—'}
                        </td>
                      </tr>
                    ))}
                    {logRows.length === 0 && (
                      <tr><td colSpan={3} style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>No searches found</td></tr>
                    )}
                  </tbody>
                </table>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 50 }}>#</th>
                      <th>User</th>
                      <th>Email</th>
                      <th>Location</th>
                      <th>Actions</th>
                      <th>Last Active</th>
                      <th style={{ width: 100 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u, i) => (
                      <tr key={u.user_id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/users/${u.user_id}`)}>
                        <td>{i + 1}</td>
                        <td style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {u.photo_url ? (
                            <img
                              src={u.photo_url}
                              alt=""
                              style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                            />
                          ) : (
                            <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'rgba(255,255,255,0.4)', flexShrink: 0 }}>
                              {(u.firstName?.[0] || '?').toUpperCase()}
                            </div>
                          )}
                          {u.firstName} {u.lastName}
                        </td>
                        <td style={{ color: 'rgba(255,255,255,0.5)' }}>{u.email || '—'}</td>
                        <td style={{ color: 'rgba(255,255,255,0.5)' }}>{u.location || '—'}</td>
                        <td>
                          <span onClick={(e) => { e.stopPropagation(); navigate(`/users/${u.user_id}?tab=activity`); }} style={{ cursor: 'pointer', color: colors.brand, fontWeight: 600 }}>
                            {u.action_count}
                          </span>
                        </td>
                        <td>{formatDate(u.last_action)}</td>
                        <td>
                          <button
                            onClick={() => navigate(`/users/${u.user_id}`)}
                            className="text-link">
                            View Profile
                          </button>
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr><td colSpan={7} style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>No users found</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </article>
      </div>
    </main>
  );
};

export default AnalyticsDetail;
